import { Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { realTestimonials, GOOGLE_REVIEWS_URL, type Testimonial } from "@/data/testimonials";

interface TestimonialCardsProps {
  testimonials?: Testimonial[];
  title?: string;
  subtitle?: string;
}

export const TestimonialCards = ({ 
  testimonials = realTestimonials,
  title = "What Our Clients Say",
  subtitle = "Real Google reviews from business owners we've worked with"
}: TestimonialCardsProps) => {
  return (
    <section className="py-12 md:py-16">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">{title}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">{subtitle}</p>
          
          {/* Clickable 5 stars on Google header */}
          <a 
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span>5 stars on Google</span>
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: testimonial.rating }, (_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-muted-foreground mb-4 italic">"{testimonial.quote}"</p>
                
                <div className="pt-4 border-t">
                  <p className="font-semibold">— {testimonial.name}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Read all reviews button */}
        <div className="text-center mt-8">
          <a 
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              Read all reviews on Google
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
